import { ArrowUpRight, Award, MapPin, Mail, Calendar, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { SITE, ALEX_PHOTO } from "@/data/site";
import { AGGREGATE } from "@/data/reviews";
import { useLiveStats } from "@/lib/liveStats";

// The WVE Team — photos hot-linked from the official site
// (https://www.weddingvideo.co.uk/about). Bios kept tight to verifiable facts only.
const TEAM_GROUP_PHOTO =
  "https://static.wixstatic.com/media/1fe187_8bb3b3fc11c949f7bb3fccd9fe868529~mv2.jpg/v1/crop/x_687,y_0,w_5302,h_4452/fill/w_1600,h_1344,al_c,q_85,enc_auto/WVE-TEAM-PHOTO.jpg";

const TEAM = [
  {
    name: "Alex Moore",
    role: "Founder · Head Videographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_c96ade0f4d2f43c6bc7bd9a10bf59fee~mv2.jpg/v1/crop/x_1260,y_0,w_3666,h_4126/fill/w_700,h_788,al_c,q_85,enc_auto/Alex-TEAM-PHOTO-WVE.jpg",
    bio: "Founded Wedding Video Essex in 2015. Personally leads every booking from first enquiry to final film delivery.",
  },
  {
    name: "Ellis Goward",
    role: "Videographer & Photographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_451802347d194e08b9aff9bef88309b1~mv2.jpg/v1/crop/x_1196,y_0,w_4037,h_4541/fill/w_700,h_788,al_c,q_85,enc_auto/ELLIS-TEAM-PHOTO-WVE.jpg",
    bio: "Met Alex at college in 2015 and has been part of the team ever since. Dual-skilled across cinema cameras and stills.",
  },
  {
    name: "Shannon Barrett",
    role: "Head Editor & Videographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_ea4823a578234aaa96c2d6a1fe27d371~mv2.jpg/v1/crop/x_1426,y_0,w_4151,h_4672/fill/w_700,h_788,al_c,q_85,enc_auto/SHANNON-TEAM-PHOTO-WVE.jpg",
    bio: "Leads the edit suite. Behind the long-form cinematic films couples actually rewatch on every anniversary.",
  },
  {
    name: "Amie Evans",
    role: "Videographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_b445f84e474d48caaecdc648dc20d990~mv2.jpg/v1/crop/x_1246,y_0,w_3803,h_4277/fill/w_700,h_788,al_c,q_85,enc_auto/AMIE-TEAM-PHOTO-WVE.jpg",
    bio: "Second-shooter on multi-camera wedding days. Specialist in ceremony coverage and bridal-prep storytelling.",
  },
  {
    name: "Andy Thomas",
    role: "Photographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_b4bcb01ce0fd4d69bd6112e2c785b2b1~mv2.jpg/v1/crop/x_1259,y_0,w_3663,h_4123/fill/w_700,h_788,al_c,q_85,enc_auto/ANDY-TEAM-PHOTO-WVE.jpg",
    bio: "The stills half of our all-inclusive Video & Photo packages. Calm, unobtrusive on the day, brilliant in low light.",
  },
  {
    name: "Lily Wright",
    role: "Videographer",
    photo:
      "https://static.wixstatic.com/media/1fe187_d630ef3202814fab9a919b8a8c87e28d~mv2.jpg/v1/crop/x_1365,y_0,w_4259,h_4791/fill/w_700,h_788,al_c,q_85,enc_auto/LILY-TEAM-PHOTO-WVE.jpg",
    bio: "Newest videographer on the team. Quietly excellent at capturing the small in-between moments that make the final cut sing.",
  },
];

export default function About() {
  const stats = useLiveStats();
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "@id": `${SITE.url}/about#aboutpage`,
      url: `${SITE.url}/about`,
      name: "About Wedding Video Essex Reviews",
      description:
        "About the people behind Wedding Video Essex: founder Alex Moore, the team, and the editorial methodology this independent review hub uses to verify every review.",
      dateModified: "2026-06-02",
      mainEntity: {
        "@type": "Person",
        "@id": `${SITE.url}/about#alex-moore`,
        name: "Alex Moore",
        jobTitle: "Founder, Lead Videographer & Photographer & Photographer",
        description:
          "Alex Moore is the founder of Wedding Video Essex. With 12+ years of wedding-specific filming experience and 1000+ Essex weddings filmed since 2015, Alex personally leads every booking from first enquiry through to film delivery.",
        image: ALEX_PHOTO,
        worksFor: {
          "@type": "Organization",
          name: "Wedding Video Essex",
          url: "https://www.weddingvideo.co.uk/",
        },
        sameAs: [
          "https://www.instagram.com/weddingvideoessex/",
          "https://www.facebook.com/essexweddingvideo",
        ],
        award: [
          "Fellow, Society of Wedding & Portrait Photographers (SWPP)",
          "International Wedding Photographer of the Year (2x)",
          "International Portrait Photographer of the Year (3x)",
          "Top 10 World's Best Wedding Photographers (2019)",
          "East of England Wedding Photographer of the Year (4x)",
          "International Judge, SWPP",
          "Essex Wedding Awards Videographer of the Year",
          "Hitched Wedding Awards Winner 2022, 2023, 2024, 2025, 2026",
          "Bridebook Platinum 2026",
        ],
        knowsAbout: [
          "Wedding videography",
          "Wedding cinematography",
          "Wedding photography",
          "Drone videography",
          "Essex wedding venues",
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE.url}/about#wve-team`,
      name: "Wedding Video Essex",
      url: "https://www.weddingvideo.co.uk/",
      image: TEAM_GROUP_PHOTO,
      employee: TEAM.map((t) => ({
        "@type": "Person",
        name: t.name,
        jobTitle: t.role,
        image: t.photo,
        worksFor: {
          "@type": "Organization",
          name: "Wedding Video Essex",
          url: "https://www.weddingvideo.co.uk/",
        },
      })),
    },
  ];

  return (
    <>
      <Seo
        title="About Alex Moore and the Wedding Video Essex Team | Wedding Video Essex Reviews"
        description={`Meet Alex Moore, founder of Wedding Video Essex, and learn how this independent review hub verifies every one of the ${stats.total} five-star reviews across Hitched, Google, Facebook, Bridebook and Guides for Brides.`}
        canonical={`${SITE.url}/about`}
        ogImage={ALEX_PHOTO}
        jsonLd={jsonLd}
      />

      {/* Hero / author profile */}
      <section className="py-20 md:py-32 bg-stone-50">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative aspect-[4/5] md:aspect-[3/4] overflow-hidden">
            <img
              src={ALEX_PHOTO}
              alt="Alex Moore, founder and lead wedding videographer and photographer at Wedding Video Essex, photographed on a wedding day in Essex"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              width="800"
              height="1067"
            />
          </div>

          <article className="md:col-span-7" itemScope itemType="https://schema.org/Person">
            <nav className="text-xs text-stone-500 mb-8 font-serif italic" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-stone-900">Home</Link>
              <span className="mx-2 text-stone-300">·</span>
              <span className="text-stone-700">About</span>
            </nav>

            <p className="overline mb-7">About the team</p>
            <h1 className="font-serif text-[2.6rem] sm:text-[3.4rem] md:text-[4.6rem] leading-[1.04] tracking-tight text-stone-900">
              Meet <em itemProp="name" className="italic font-normal text-stone-600">Alex Moore.</em>
            </h1>
            <p
              itemProp="jobTitle"
              className="mt-5 font-serif italic text-stone-600 text-xl"
            >
              Founder &amp; Lead Wedding Videographer & Photographer · Wedding Video Essex
            </p>

            <p
              itemProp="description"
              className="mt-8 font-serif text-stone-800 text-[1.18rem] md:text-[1.25rem] leading-[1.65]"
            >
              Alex founded Wedding Video Essex in 2015 with a simple belief: every couple deserves a
              film that makes them feel the day all over again. Over 1000 Essex couples later, he
              still personally leads every booking from the very first call to the moment your final
              film is delivered. No call centres. No being passed between account managers.
              One person, start to finish.
            </p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-6">
              <div>
                <Camera className="w-5 h-5 text-stone-500 mb-2" strokeWidth={1.2} />
                <p className="font-serif text-stone-900 text-3xl kbd leading-none">1000+</p>
                <p className="overline text-stone-500 mt-2">Weddings filmed</p>
              </div>
              <div>
                <Calendar className="w-5 h-5 text-stone-500 mb-2" strokeWidth={1.2} />
                <p className="font-serif text-stone-900 text-3xl kbd leading-none">12+</p>
                <p className="overline text-stone-500 mt-2">Years filming</p>
              </div>
              <div>
                <Award className="w-5 h-5 text-stone-500 mb-2" strokeWidth={1.2} />
                <p className="font-serif text-stone-900 text-3xl kbd leading-none">8x</p>
                <p className="overline text-stone-500 mt-2">Award winners</p>
              </div>
              <div>
                <MapPin className="w-5 h-5 text-stone-500 mb-2" strokeWidth={1.2} />
                <p className="font-serif text-stone-900 text-3xl kbd leading-none">SS11</p>
                <p className="overline text-stone-500 mt-2">Based in Wickford</p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="about-cta-contact-alex"
              >
                Contact Alex directly
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <Link to="/reviews" className="link-elegant" data-testid="about-cta-reviews">
                Read all {stats.total} verified reviews
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* Credentials and experience */}
      <section className="py-20 md:py-28 bg-[#F5F0EA] grain relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="overline mb-7">Credentials and experience</p>
          <h2 className="font-serif text-[2.2rem] md:text-[3.2rem] leading-[1.06] tracking-tight text-stone-900 max-w-3xl">
            Twelve years filming Essex weddings.
            <br />
            <em className="italic font-normal text-stone-600">Recognised by every major industry body.</em>
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-x-16 gap-y-10">
            <article>
              <h3 className="font-serif italic text-stone-900 text-2xl mb-4">Selected honours</h3>
              <ul className="space-y-3 font-serif text-stone-800 text-[1.1rem] leading-relaxed">
                <li>Fellow, Society of Wedding &amp; Portrait Photographers (SWPP)</li>
                <li>2× International Wedding Photographer of the Year</li>
                <li>3× International Portrait Photographer of the Year</li>
                <li>Top 10 World&apos;s Best Wedding Photographers (2019)</li>
                <li>4× East of England Wedding Photographer of the Year</li>
                <li>International Judge, SWPP</li>
                <li>Essex Wedding Awards — Videographer of the Year</li>
                <li>Hitched Wedding Awards Winner 2022, 2023, 2024, 2025, 2026</li>
                <li>Bridebook Platinum 2026</li>
              </ul>
            </article>

            <article>
              <h3 className="font-serif italic text-stone-900 text-2xl mb-4">Specialist experience</h3>
              <ul className="space-y-3 font-serif text-stone-800 text-[1.1rem] leading-relaxed">
                <li>1000+ Essex weddings filmed since 2015</li>
                <li>Recommended supplier at 21+ Essex wedding venues</li>
                <li>Trained on Canon C100 Mark II cinema cameras</li>
                <li>CAA-compliant licensed drone operator</li>
                <li>Specialist in long-form cinematic edits (20–30 minutes)</li>
                <li>Full backup equipment, fully insured</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* One team, both crafts */}
      <section className="py-20 md:py-28 bg-stone-50" data-testid="photo-video-team-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="overline mb-7">One team, both crafts</p>
          <h2 className="font-serif text-[2.2rem] md:text-[3.2rem] leading-[1.06] tracking-tight text-stone-900 max-w-3xl">
            A photo and video team.
            <br />
            <em className="italic font-normal text-stone-600">Not two suppliers who just met.</em>
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-x-16 gap-y-8 max-w-4xl">
            <p className="font-serif text-stone-700 text-[1.15rem] leading-[1.7]">
              Wedding Video Essex shoots both photography and videography with one
              team that works together every weekend. Couples in our reviews
              consistently mention how seamlessly the two work side by side — no
              jostling for the same shot, no clashing directions, no wondering whose
              timeline you are on.
            </p>
            <p className="font-serif text-stone-700 text-[1.15rem] leading-[1.7]">
              Because the same team edits both, your photos and your film share one
              consistent visual style — the same colour, the same tone, the same
              story. One point of contact from first enquiry to final delivery, for
              both crafts.
            </p>
          </div>
        </div>
      </section>

      {/* The team */}
      <section className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <p className="overline mb-7">The team behind the films</p>
            <h2 className="font-serif text-[2.2rem] md:text-[3.2rem] leading-[1.06] tracking-tight text-stone-900">
              The Wedding Video Essex team.
              <br />
              <em className="italic font-normal text-stone-600">Six people. One day. Yours.</em>
            </h2>
            <p className="mt-7 font-serif text-stone-700 text-[1.15rem] md:text-[1.2rem] leading-[1.65] max-w-2xl">
              Wedding Video Essex isn&apos;t a faceless studio. It&apos;s a small,
              long-tenured team — most of whom have worked together since 2015 — quietly
              filming and editing the films and photos that earn the reviews on this site.
            </p>
          </div>

          {/* Group photo */}
          <figure className="mt-14 md:mt-20 relative aspect-[5/4] md:aspect-[3/2] overflow-hidden bg-stone-100">
            <img
              src={TEAM_GROUP_PHOTO}
              alt="The Wedding Video Essex team — Alex Moore, Ellis Goward, Shannon Barrett, Amie Evans, Andy Thomas and Lily Wright — photographed together at the studio in Wickford, Essex"
              className="absolute inset-0 w-full h-full object-contain"
              loading="lazy"
              width="1600"
              height="900"
              data-testid="about-team-group-photo"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-stone-900/70 to-transparent px-6 md:px-10 py-6 md:py-8">
              <p className="font-serif italic text-stone-50 text-base md:text-lg">
                The team at the Wickford studio.
              </p>
            </figcaption>
          </figure>

          {/* Individual portraits */}
          <ul className="mt-20 md:mt-28 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 md:gap-x-14 gap-y-16 md:gap-y-24">
            {TEAM.map((m) => (
              <li
                key={m.name}
                data-testid={`about-team-member-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                itemScope
                itemType="https://schema.org/Person"
              >
                <figure className="relative aspect-[4/5] overflow-hidden bg-stone-100">
                  <img
                    src={m.photo}
                    alt={`${m.name}, ${m.role} at Wedding Video Essex`}
                    itemProp="image"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                    loading="lazy"
                    width="700"
                    height="875"
                  />
                </figure>
                <h3
                  itemProp="name"
                  className="mt-6 font-serif text-stone-900 text-[1.65rem] md:text-[1.85rem] leading-tight"
                >
                  {m.name}
                </h3>
                <p
                  itemProp="jobTitle"
                  className="mt-1.5 overline text-stone-500"
                >
                  {m.role}
                </p>
                <p
                  itemProp="description"
                  className="mt-4 font-serif text-stone-700 text-[1.02rem] leading-[1.6]"
                >
                  {m.bio}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Editorial methodology / About this site */}
      <section className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="overline mb-7">Editorial methodology</p>
          <h2 className="font-serif text-[2.2rem] md:text-[3rem] leading-[1.06] tracking-tight text-stone-900">
            How we verify every review on this site.
          </h2>

          <article className="mt-10 font-serif text-stone-800 text-[1.15rem] leading-[1.7] space-y-6">
            <p>
              <strong>weddingvideoessexreviews.co.uk</strong> is an independent review aggregation
              hub for <a href="https://www.weddingvideo.co.uk/" rel="noopener" target="_blank" className="border-b border-stone-300 hover:border-stone-700">Wedding Video Essex</a>, a member of the <a
                href="https://www.wvegroup.co.uk/"
                rel="noopener"
                target="_blank"
                className="border-b border-stone-300 hover:border-stone-700"
              >
                WVE Group
              </a>. We exist so couples, search engines and AI assistants can verify Wedding Video
              Essex&apos;s review record without checking four separate platforms individually.
            </p>

            <h3 className="font-serif italic text-stone-900 text-xl mt-10 mb-2">1. Sourcing</h3>
            <p>
              Every review on this site is taken directly from a publicly published review on one of
              four primary platforms: <a href="https://www.hitched.co.uk/wedding-videographers/wedding-video-essex-films-&-photos_272043.htm" rel="noopener" target="_blank" className="border-b border-stone-300 hover:border-stone-700">Hitched.co.uk</a>, <a href="https://www.google.com/maps/place/?q=place_id:ChIJCTdbENbD2EcRzWdysOypqHo" rel="noopener" target="_blank" className="border-b border-stone-300 hover:border-stone-700">Google Maps</a>, <a href="https://www.facebook.com/essexweddingvideo/reviews" rel="noopener" target="_blank" className="border-b border-stone-300 hover:border-stone-700">Facebook recommendations</a>, and <a href="https://bridebook.com/uk/wedding-videographers/wedding-video-essex-wickford-essex-csNg0Nqz78" rel="noopener" target="_blank" className="border-b border-stone-300 hover:border-stone-700">Bridebook</a>.
            </p>

            <h3 className="font-serif italic text-stone-900 text-xl mt-10 mb-2">2. Attribution</h3>
            <p>
              Every review card on this site contains the reviewer&apos;s real first name, the date
              of publication, and a direct link back to the original public review on its source
              platform. Readers can independently verify any claim made on this site by clicking the
              source link.
            </p>

            <h3 className="font-serif italic text-stone-900 text-xl mt-10 mb-2">3. What we do not do</h3>
            <p>
              We do not fabricate reviews. We do not edit the substance of reviews (only minor
              punctuation for typographic consistency). We do not pay for reviews. We do not
              incentivise reviews in exchange for discounts or freebies. We do not remove negative
              reviews — Wedding Video Essex&apos;s 100% recommendation rate is the genuine outcome
              of {stats.total} independent reviews across five platforms.
            </p>

            <h3 className="font-serif italic text-stone-900 text-xl mt-10 mb-2">4. Aggregate counts</h3>
            <p>
              The aggregate count and rating shown on this site are the sum of the publicly verified
              review counts on each platform on the date of last update. The current live breakdown
              is: {stats.hitchedCount} on Hitched, {stats.googleCount} on Google,{" "}
              {stats.facebookCount} on Facebook, {stats.bridebookCount} on Bridebook, and{" "}
              {stats.guidesForBridesCount} on Guides for Brides, for a total of {stats.total}{" "}
              five-star reviews.
            </p>
          </article>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 md:py-28 bg-stone-900 text-stone-50">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="overline text-stone-500 mb-7">Contact Alex and the team</p>
            <h2 className="font-serif text-[2.2rem] md:text-[3rem] leading-[1.06] tracking-tight text-stone-50">
              Speak to the person who&apos;ll
              <br />
              <em className="italic font-normal text-stone-400">actually film your wedding.</em>
            </h2>
          </div>

          <address className="md:col-span-6 not-italic font-serif text-stone-300 text-[1.1rem] leading-relaxed space-y-5">
            <div>
              <p className="overline text-stone-500 mb-2">Studio</p>
              <p>Wedding Video Essex</p>
              <p>Unit 6 Hodgson Court, Hodgson Way</p>
              <p>Wickford, Essex SS11 8XR</p>
              <p>United Kingdom</p>
            </div>
            <div>
              <p className="overline text-stone-500 mb-2">Speak to Alex directly</p>
              <p>
                <a
                  href="tel:+441277280512"
                  className="border-b border-stone-700 hover:border-stone-200 pb-0.5"
                  data-testid="about-phone-link"
                >
                  01277 280 512
                </a>
              </p>
              <p className="mt-3">
                <Mail className="inline w-4 h-4 mr-2 -mt-0.5" strokeWidth={1.2} />
                <a
                  href="mailto:alex@weddingvideo.co.uk"
                  className="border-b border-stone-700 hover:border-stone-200 pb-0.5"
                  data-testid="about-email-link"
                >
                  alex@weddingvideo.co.uk
                </a>
              </p>
              <p className="mt-3">
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-b border-stone-700 hover:border-stone-200 pb-0.5"
                  data-testid="about-contact-page-link"
                >
                  Use the Wedding Video Essex contact form
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </p>
            </div>
            <div>
              <p className="overline text-stone-500 mb-2">Service area</p>
              <p>Essex, London, Kent and Suffolk</p>
            </div>
          </address>
        </div>
      </section>
    </>
  );
}
